import { useEffect, useMemo, useRef } from 'react';

import {
	type AgentRunState,
	useAgentRuns,
} from '@atlassian/conversation-assistant-store/controllers/agent-mention-run-state';

import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { expValEquals } from '@confluence/feature-experiments';

// Stable reference so the memo returns the same empty map while the feature is off (avoids
// re-running the effect on unrelated renders)
const EMPTY_RUN_STATE_MAP: Record<string, AgentRunState> = {};

/**
 * Reads the agent run-state from agent-run-state bridge store (keyed by conversationId, with state
 * and mention node localId) and pushes a `localId -> run-state` map into the mentions plugin via
 * the action `setAgentMentionRunStates`.
 *
 * Session-based: the bridge store is in-memory, so a page refresh starts empty.
 */
export const useAgentRunStateBridge = (
	fullPageEditorPresetAPI: FullPageEditorPresetAPI | undefined,
): void => {
	const isEnabled =
		expValEquals('platform_editor_agent_state_bridge', 'isEnabled', true) &&
		expValEquals('platform_editor_agent_mention_state_anim', 'isEnabled', true);

	const runs = useAgentRuns();
	const lastSentRef = useRef<{
		api: FullPageEditorPresetAPI;
		runStateByLocalId: Record<string, AgentRunState>;
	} | null>(null);

	const runStateByLocalId = useMemo(() => {
		if (!isEnabled) {
			return EMPTY_RUN_STATE_MAP;
		}

		const map: Record<string, AgentRunState> = {};
		for (const record of Object.values(runs)) {
			if (
				record.invokedByNodeLocalId &&
				(record.state === 'thinking' || record.state === 'working')
			) {
				map[record.invokedByNodeLocalId] = record.state;
			}
		}
		return map;
	}, [runs, isEnabled]);

	useEffect(() => {
		if (!isEnabled || !fullPageEditorPresetAPI?.mention) {
			return;
		}

		const lastSent = lastSentRef.current;
		const localIds = Object.keys(runStateByLocalId);
		// An empty store needs no editor transaction until there is an animation to clear
		if (
			localIds.length === 0 &&
			(!lastSent || Object.keys(lastSent.runStateByLocalId).length === 0)
		) {
			return;
		}
		// Skip when this editor already has the same visible run states
		if (
			lastSent?.api === fullPageEditorPresetAPI &&
			localIds.length === Object.keys(lastSent.runStateByLocalId).length &&
			localIds.every(
				(localId) => lastSent.runStateByLocalId[localId] === runStateByLocalId[localId],
			)
		) {
			return;
		}

		if (fullPageEditorPresetAPI.mention.actions.setAgentMentionRunStates(runStateByLocalId)) {
			lastSentRef.current = { api: fullPageEditorPresetAPI, runStateByLocalId };
		}
	}, [isEnabled, fullPageEditorPresetAPI, runStateByLocalId]);
};
